import type {
	EventReanalysis,
	EventReanalysisView,
	ReanalysisSuggestion,
	RequestReanalysisInput,
	ShadowEventSummary,
	VerificationVideo,
} from "@sitescene/types";

import {
	EventReanalysisOutcome,
	EventReanalysisStatus,
} from "@sitescene/types";
import {
	type InfiniteData,
	type QueryClient,
	useInfiniteQuery,
	useMutation,
	useQuery,
	useQueryClient,
} from "@tanstack/react-query";

import { apiClient, ApiError, type ApiResponse } from "@/api/client";
import { ENDPOINTS } from "@/api/endpoints";
import { queryKeys } from "@/api/query-keys";
import { useOrgSelection } from "@/context/use-org-selection";
import { isConflictError } from "@/features/scene-facts/use-scene-proposals";

export interface ReanalysisActionInput {
	/**
	 * `check` asks about one taking too long (the server closes it if it is
	 * stuck); `force` applies a `notConfirmed` result anyway; `revert` undoes
	 * an applied one.
	 */
	action: "check" | "force" | "revert";
	reanalysisId: string;
}

export interface ReanalysisFilters {
	kind?: string;
	outcome?: string;
	status?: string;
}

const PAGE_SIZE = 50;
const POLL_MS = 5000;
/**
 * How long to poll an open reanalysis before calling it overdue. The worker
 * reports a failure, but a message lost on the way would otherwise keep the
 * page polling for as long as it stays open; past this the page says so and
 * offers a manual check instead.
 */
const POLL_LIMIT_MS = 15 * 60 * 1000;

const { Completed, Queued, Running } = EventReanalysisStatus;

/** Queued or running: the worker has not finished with it. */
export const isReanalysisOpen = (
	reanalysis: Pick<EventReanalysis, "status">,
): boolean => reanalysis.status === Queued || reanalysis.status === Running;

/**
 * Still open long after the worker should have finished, as of `checkedAt`
 * (epoch ms). Pass the query's `dataUpdatedAt`, so the answer changes only
 * when the data does and the page and the poll agree on it. Counted from
 * Apply anyway on a forced row: that re-queues the same row, possibly long
 * after it was requested.
 */
export const isReanalysisOverdue = (
	reanalysis: Pick<EventReanalysis, "forced" | "requestedAt" | "status">,
	checkedAt: number,
): boolean =>
	isReanalysisOpen(reanalysis) &&
	checkedAt -
		new Date(reanalysis.forced?.at ?? reanalysis.requestedAt).getTime() >=
		POLL_LIMIT_MS;

const worthPolling = (reanalysis: EventReanalysis, checkedAt: number) =>
	isReanalysisOpen(reanalysis) && !isReanalysisOverdue(reanalysis, checkedAt);

/**
 * The one reanalysis Revert is offered on: the newest that was applied and
 * still stands. Undoing an older one would write back what the event said
 * before a correction that has since been built on. Expects newest first.
 */
export const revertableId = (
	reanalyses: readonly EventReanalysis[],
): string | undefined =>
	reanalyses.find((r) => r.appliedAt && r.status === Completed)?.id;

/** A finished result the footage did not support, not yet forced through. */
export const canApplyAnyway = (reanalysis: EventReanalysis): boolean =>
	reanalysis.status === Completed &&
	reanalysis.outcome === EventReanalysisOutcome.NotConfirmed &&
	!reanalysis.forced;

/** A 404: nothing by that id in the selected organization. */
export const isNotFoundError = (error: unknown): boolean =>
	error instanceof ApiError && error.statusCode === 404;

const useOrgCode = () => {
	const { selected } = useOrgSelection();
	return selected?.orgCode ?? null;
};

/**
 * Refresh what a reanalysis can change: the event as it now reads, its
 * history, and every tracking list of the organization. Not the clip URL:
 * re-signing it would reload the player mid-playback.
 */
const invalidateEvent = (
	queryClient: QueryClient,
	orgCode: string,
	eventId: string,
) =>
	Promise.all([
		queryClient.invalidateQueries({
			queryKey: queryKeys.reanalysis.event(orgCode, eventId),
		}),
		queryClient.invalidateQueries({
			queryKey: queryKeys.reanalysis.view(orgCode, eventId),
		}),
		queryClient.invalidateQueries({
			queryKey: ["shadow", "reanalysis", "list", orgCode],
		}),
	]);

/**
 * Every reanalysis of the selected organization, from either app, newest
 * first, paged by cursor. Polled while a row on screen is still queued or
 * running, so the list settles on its own.
 */
export const useReanalyses = (filters: ReanalysisFilters) => {
	const orgCode = useOrgCode();
	const activeFilters = Object.fromEntries(
		Object.entries(filters).filter(([, value]) => Boolean(value)),
	) as Record<string, string>;

	return useInfiniteQuery<
		ApiResponse<EventReanalysis[]>,
		Error,
		InfiniteData<ApiResponse<EventReanalysis[]>>,
		readonly unknown[],
		string | undefined
	>({
		enabled: orgCode !== null,
		getNextPageParam: (lastPage) =>
			lastPage.hasMore ? lastPage.endCursor : undefined,
		initialPageParam: undefined,
		queryFn: async ({ pageParam }) => {
			const response = await apiClient.get<EventReanalysis[]>(
				ENDPOINTS.shadow.reanalyses,
				{
					params: { ...activeFilters, after: pageParam, limit: PAGE_SIZE },
				},
			);
			return response;
		},
		queryKey: queryKeys.reanalysis.list(orgCode ?? "", activeFilters),
		refetchInterval: (query) => {
			const rows = query.state.data?.pages.flatMap((page) => page.data) ?? [];
			return rows.some((row) => worthPolling(row, query.state.dataUpdatedAt))
				? POLL_MS
				: false;
		},
	});
};

/**
 * The event as the lookup shows it. A 404 means it is not in the selected
 * organization, and asking again will not change that.
 */
export const useShadowEvent = (eventId: string | undefined) => {
	const orgCode = useOrgCode();

	return useQuery({
		enabled: orgCode !== null && Boolean(eventId),
		queryFn: async () => {
			const response = await apiClient.get<ShadowEventSummary>(
				ENDPOINTS.shadow.events.event(eventId ?? ""),
			);
			return response.data;
		},
		queryKey: queryKeys.reanalysis.event(orgCode ?? "", eventId ?? ""),
		retry: (failureCount, error) => !isNotFoundError(error) && failureCount < 3,
	});
};

/**
 * A signed playback URL for the event's clip, held for most of its hour like
 * the verification clip.
 */
export const useShadowEventVideo = (eventId: string | undefined) => {
	const orgCode = useOrgCode();

	return useQuery({
		enabled: orgCode !== null && Boolean(eventId),
		gcTime: 0,
		queryFn: async () => {
			const response = await apiClient.get<VerificationVideo>(
				ENDPOINTS.shadow.events.video(eventId ?? ""),
			);
			return response.data;
		},
		queryKey: queryKeys.reanalysis.eventVideo(orgCode ?? "", eventId ?? ""),
		retry: false,
		staleTime: 50 * 60 * 1000,
	});
};

/**
 * The correction panel's context and the event's reanalyses, newest first.
 * The run finishes in a worker — a Gemini video call can outlast API Gateway
 * — so the page polls while the newest is queued or running, until it lands
 * or is overdue. A run that just landed may have rewritten the event, so its
 * summary and the tracking lists are refreshed then too.
 */
export const useEventReanalysisView = (eventId: string | undefined) => {
	const orgCode = useOrgCode();
	const queryClient = useQueryClient();
	const queryKey = queryKeys.reanalysis.view(orgCode ?? "", eventId ?? "");

	return useQuery({
		enabled: orgCode !== null && Boolean(eventId),
		queryFn: async () => {
			const previous = queryClient.getQueryData<EventReanalysisView>(queryKey);
			const response = await apiClient.get<EventReanalysisView>(
				ENDPOINTS.shadow.events.reanalysis(eventId ?? ""),
			);
			const landed =
				previous?.reanalyses.some(isReanalysisOpen) &&
				!response.data.reanalyses.some(isReanalysisOpen);
			if (landed) {
				void queryClient.invalidateQueries({
					queryKey: queryKeys.reanalysis.event(orgCode ?? "", eventId ?? ""),
				});
				void queryClient.invalidateQueries({
					queryKey: ["shadow", "reanalysis", "list", orgCode ?? ""],
				});
			}
			return response.data;
		},
		queryKey,
		refetchInterval: (query) => {
			const newest = query.state.data?.reanalyses[0];
			return newest && worthPolling(newest, query.state.dataUpdatedAt)
				? POLL_MS
				: false;
		},
	});
};

/**
 * Read which class staff's words describe. A suggestion only: nothing is
 * written until they submit, so there is no cache to invalidate.
 */
export const useSuggestReanalysisClass = (eventId: string) =>
	useMutation({
		mutationFn: async (whatHappened: string) => {
			const response = await apiClient.post<ReanalysisSuggestion>(
				ENDPOINTS.shadow.events.reanalysisSuggest(eventId),
				{ whatHappened },
			);
			return response.data;
		},
	});

/**
 * Queue a reanalysis. Resolves with the new `queued` row; the view refetches
 * with it on top, and its poll takes over from there.
 */
export const useRequestReanalysis = (eventId: string) => {
	const queryClient = useQueryClient();
	const orgCode = useOrgCode() ?? "";

	return useMutation({
		mutationFn: async (input: RequestReanalysisInput) => {
			const response = await apiClient.post<EventReanalysis>(
				ENDPOINTS.shadow.events.reanalysis(eventId),
				input,
			);
			return response.data;
		},
		onError: async (error) => {
			// Most often another reanalysis of this event started first — someone
			// else pressed Submit. Refresh so it shows beside the message.
			if (isConflictError(error)) {
				await invalidateEvent(queryClient, orgCode, eventId);
			}
		},
		onSuccess: async () => {
			await invalidateEvent(queryClient, orgCode, eventId);
		},
	});
};

/**
 * Apply a `notConfirmed` result anyway, revert an applied one, or check on
 * one taking too long.
 */
export const useReanalysisAction = (eventId: string) => {
	const queryClient = useQueryClient();
	const orgCode = useOrgCode() ?? "";

	return useMutation({
		mutationFn: async ({ action, reanalysisId }: ReanalysisActionInput) => {
			const response = await apiClient.post<EventReanalysis>(
				ENDPOINTS.shadow.events.reanalysisAction(eventId, reanalysisId, action),
			);
			return response.data;
		},
		onError: async (error) => {
			// The page is behind the server: someone else acted on it first.
			if (isConflictError(error)) {
				await invalidateEvent(queryClient, orgCode, eventId);
			}
		},
		onSuccess: async () => {
			await invalidateEvent(queryClient, orgCode, eventId);
		},
	});
};

/**
 * The event a detection belongs to, for the cross-link from a verification.
 * Looked up on demand, and cached so the event page opens on it at once.
 */
export const useFindEventByMeasurement = () => {
	const queryClient = useQueryClient();
	const orgCode = useOrgCode() ?? "";

	return useMutation({
		mutationFn: async (measurementId: string) => {
			const response = await apiClient.get<ShadowEventSummary>(
				ENDPOINTS.shadow.events.byMeasurement(measurementId),
			);
			return response.data;
		},
		onSuccess: (event) => {
			queryClient.setQueryData(
				queryKeys.reanalysis.event(orgCode, event.id),
				event,
			);
		},
	});
};
